/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the Array type V as an input, and returns either:
 *                  - a single value of type K, or
 *                  - an array of values of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
export function groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K | K[]): Map<K, Array<V>> {
  const map = new Map<K, Array<V>>();

  list.forEach((item) => {
    const keys = keyGetter(item);
    const keyArray = Array.isArray(keys) ? keys : [keys]; // Ensure keys is always an array

    keyArray.forEach((key) => {
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
  });

  return map;

  // example usage

  // const pets = [
  //   { type: "Dog", name: "Spot" },
  //   { type: "Cat", name: "Tiger" },
  //   { type: "Dog", name: "Rover" },
  //   { type: "Cat", name: "Leo" },
  //   { type: "Bird", name: "Tweety" },
  // ];

  // const grouped = groupBy(pets, (pet) => pet.type);

  // console.log(grouped.get("Dog")); // -> [{type:"Dog", name:"Spot"}, {type:"Dog", name:"Rover"}]
  // console.log(grouped.get("Cat")); // -> [{type:"Cat", name:"Tiger"}, {type:"Cat", name:"Leo"}]

  // const groupedByMultipleKeys = groupBy(pets, (pet) =>
  //   pet.type === "Dog" ? ["Dog", "Animal"] : ["Animal"]
  // );

  // console.log(groupedByMultipleKeys.get("Dog")); // -> [{type:"Dog", name:"Spot"}, {type:"Dog", name:"Rover"}]
  // console.log(groupedByMultipleKeys.get("Animal")); // -> [{type:"Dog", name:"Spot"}, {type:"Dog", name:"Rover"}, {type:"Cat", name:"Tiger"}, {type:"Cat", name:"Leo"}, {type:"Bird", name:"Tweety"}]
}
